import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAccessControl } from "@/hooks/useAccessControl";
import useAuth from "@/hooks/useAuth";
import { searchContentHubByTree } from "../content-hub/api";
import { CONTENT_HUB_SOURCES } from "../content-hub/config";
import type { FilterTreeNode } from "../content-hub/filter";
import { fetchCategoryHistory } from "./api";
import { toCalendarEvent } from "./mapper";
import type { CalendarEvent, CategoryHistory } from "./types";
import { localDayBoundsUtc, todayLocalDateString } from "./utils/date";

const CALENDAR_SOURCE = CONTENT_HUB_SOURCES.CALENDAR_EVENT;

/** One large page per day — a single calendar day rarely exceeds this. */
const DAY_PAGE_SIZE = 500;

/** How often to re-fetch the current day for fresh figures (no SSE here). */
const TODAY_REFETCH_MS = 30_000;

/**
 * Build the filter tree for one local day of calendar events:
 * `{ backend_source: [...], occurs_at: { gte, lt } }` — both fields on one leaf
 * are AND-ed by the server. The bounds are the selected local day's midnight
 * boundaries converted to UTC; `lt` is exclusive so the next day's midnight
 * event is not pulled in.
 */
function buildCalendarDayTree(date: string): FilterTreeNode {
  const { gte, lt } = localDayBoundsUtc(date);
  return {
    backend_source: [CALENDAR_SOURCE],
    occurs_at: { gte, lt },
  };
}

export interface UseCalendarV2EventsResult {
  events: CalendarEvent[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  /** Manually re-fetch the selected day. */
  refetch: () => void;
  /** A fetch (initial or background) is in flight. */
  isFetching: boolean;
  /** Epoch ms the data was last updated (0 before the first success). */
  dataUpdatedAt: number;
  /** UUIDs of events that changed since the last fetch (pulse targets). */
  changedEventUuids: Set<string>;
  /** Clear an event's changed status once its pulse animation has played. */
  clearChangedStatus: (uuid: string) => void;
  /** True when serving the public 24h-delayed feed (logged-out / expired). */
  isDelayed: boolean;
}

/**
 * Calendar events for a single UTC day, via the content-hub adapter.
 *
 * Fetches `POST /v1/search?size=500&sort=recent` with a day-windowed filter
 * tree (source + occurs_at range). The current day is polled for freshness
 * (no SSE on this view); past/future days are fetched once and cached. Demo
 * users (logged-out / expired) hit the public 24h-delayed endpoint. Figures
 * that change between fetches are surfaced as `changedEventUuids` so rows pulse,
 * cleared after the animation via `clearChangedStatus`.
 */
export function useCalendarV2Events(date: string): UseCalendarV2EventsResult {
  const { isInDemoMode } = useAccessControl();
  const isToday = date === todayLocalDateString();

  const tree = useMemo(() => buildCalendarDayTree(date), [date]);

  const query = useQuery({
    queryKey: ["calendar-v2", "day", date, isInDemoMode],
    queryFn: () =>
      searchContentHubByTree(tree, undefined, {
        size: DAY_PAGE_SIZE,
        delayed: isInDemoMode,
      }),
    enabled: !!date,
    // Only the current day needs to stay fresh; other days are static history.
    staleTime: isToday ? 0 : 5 * 60 * 1000,
    refetchInterval: isToday ? TODAY_REFETCH_MS : false,
    refetchOnWindowFocus: isToday,
  });

  const events = useMemo<CalendarEvent[]>(
    () =>
      (query.data?.items ?? [])
        .map(toCalendarEvent)
        .filter((e): e is CalendarEvent => e !== null)
        // Hide entries whose category is marked invisible.
        .filter(
          (e) =>
            (e as { category?: { state?: string } }).category?.state !==
            "invisible"
        ),
    [query.data]
  );

  // ── Change detection ──────────────────────────────────────────────────────
  // Track each event's updated_sequence; when it increases for an already-seen
  // event, flag it changed (first sighting is not a change). The seen-map is
  // reset whenever the selected day changes so a day switch never pulses.
  const seqRef = useRef<Map<string, number>>(new Map());
  const [changedEventUuids, setChangedEventUuids] = useState<Set<string>>(
    () => new Set()
  );

  useEffect(() => {
    seqRef.current = new Map();
    setChangedEventUuids(new Set());
  }, [date, isInDemoMode]);

  useEffect(() => {
    const seq = seqRef.current;
    const newlyChanged: string[] = [];
    for (const event of events) {
      const uuid = event.uuid;
      if (!uuid) continue;
      const sequence = event.updated_sequence ?? 0;
      const prev = seq.get(uuid);
      if (prev !== undefined && sequence > prev) newlyChanged.push(uuid);
      seq.set(uuid, sequence);
    }
    if (newlyChanged.length > 0) {
      setChangedEventUuids((current) => {
        const next = new Set(current);
        for (const uuid of newlyChanged) next.add(uuid);
        return next;
      });
    }
  }, [events]);

  const clearChangedStatus = useCallback((uuid: string) => {
    setChangedEventUuids((current) => {
      if (!current.has(uuid)) return current;
      const next = new Set(current);
      next.delete(uuid);
      return next;
    });
  }, []);

  return {
    events,
    isLoading: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: () => query.refetch(),
    isFetching: query.isFetching,
    dataUpdatedAt: query.dataUpdatedAt,
    changedEventUuids,
    clearChangedStatus,
    isDelayed: isInDemoMode,
  };
}

/** Years of history to request per category — enough for a trend sparkline. */
const HISTORY_LOOKBACK_YEARS = 5;

const EMPTY_HISTORY: Record<string, CategoryHistory> = {};

/**
 * Category history for the given data categories, keyed by category id, for the
 * row mini-charts. One request per distinct category set; cached and treated as
 * slow-moving. Disabled when `enabled` is false or there are no categories.
 */
export function useCalendarCategoryHistory(
  categoryIds: number[],
  enabled: boolean
): Record<string, CategoryHistory> {
  // The history endpoint requires a Keycloak bearer, so it's authenticated-only
  // (skipped in demo mode, where there's no token).
  const { authenticated } = useAuth();

  // Stable lookback start (computed once) so the query key doesn't churn.
  const startDate = useMemo(() => {
    const d = new Date();
    d.setUTCFullYear(d.getUTCFullYear() - HISTORY_LOOKBACK_YEARS);
    return d.toISOString();
  }, []);

  const ids = useMemo(
    () => [...new Set(categoryIds)].sort((a, b) => a - b),
    [categoryIds]
  );

  const query = useQuery({
    queryKey: ["calendar-v2", "category-history", ids, startDate],
    queryFn: () => fetchCategoryHistory(ids, startDate),
    enabled: enabled && authenticated && ids.length > 0,
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return query.data ?? EMPTY_HISTORY;
}
